import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('accessToken'));

  // Configure axios defaults
  useEffect(() => {
    // Set base URL for all API calls
    const isLocal = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
    axios.defaults.baseURL = isLocal ? 'http://localhost:3001' : '';
    
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get('/api/auth/validate');
          setUser(response.data.user);
          setIsAuthenticated(true);
        } catch (error) {
          console.error('Token validation failed:', error);
          localStorage.removeItem('accessToken');
          setToken(null);
          setUser(null);
          setIsAuthenticated(false);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [token]);

  const login = async (email, password, additionalData = {}) => {
    try {
      setLoading(true);
      const loginPayload = {
        email,
        password,
        ...additionalData // Include bot protection fields (_timestamp, _fingerprint, honeypot fields)
      };
      const response = await axios.post('/api/auth/login', loginPayload);
      
      console.log('🔍 Login response:', response.data);
      
      const { accessToken: newToken, user: userData } = response.data;
      
      console.log('🔍 Extracted token:', newToken);
      console.log('🔍 Extracted user:', userData);
      
      localStorage.setItem('accessToken', newToken);
      setToken(newToken);
      setUser(userData);
      setIsAuthenticated(true);
      
      // Set Authorization header immediately
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      console.log('🔐 Login successful, Authorization header set:', axios.defaults.headers.common['Authorization']);
      
      toast.success('Login successful!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      toast.error(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      setLoading(true);
      const response = await axios.post('/api/auth/register', userData);
      
      toast.success('Registration successful! Please log in.');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Registration failed';
      toast.error(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    // Notify server to release demo slot (fire-and-forget)
    if (token) {
      try {
        await axios.post('/api/auth/logout', {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (e) { /* ignore */ }
    }
    localStorage.removeItem('accessToken');
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    delete axios.defaults.headers.common['Authorization'];
    toast.success('Logged out successfully');
  };

  // Demo session timer: auto-logout when expired
  useEffect(() => {
    if (!user?.isDemo || !user?.demoSessionExpiresAt) return;
    const checkInterval = setInterval(() => {
      if (Date.now() >= user.demoSessionExpiresAt) {
        toast.error('Your demo session has expired');
        logout();
      }
    }, 10000); // Check every 10 seconds
    return () => clearInterval(checkInterval);
  }, [user?.isDemo, user?.demoSessionExpiresAt]);

  const updateUser = (userData) => {
    setUser(userData);
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    token,
    login,
    register,
    logout,
    updateUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
